/**
 * security.js — Admin Security API hooks
 *
 * Endpoints (backend parallel dev):
 *   GET    /admin/users/{user_id}/sessions
 *   POST   /admin/users/{user_id}/revoke-all-sessions
 *   GET    /admin/anomalies
 *   POST   /admin/anomalies/{id}/review
 *   GET    /admin/users/{user_id}/access-pattern
 *
 * Hooks (React Query):
 *   useActiveSessions(filters)          — all active sessions, filterable
 *   useRevokeUserSessions(userId)       — mutation: revoke all sessions for a user
 *   useAnomalies(filters)              — anomaly list (open/reviewed/dismissed)
 *   useReviewAnomaly()                  — mutation: dismiss / freeze_account / revoke_sessions / investigate
 *   useUserAccessPattern(userId)        — hourly access heatmap for a user
 *   useOpenAnomalyCount()               — count of open anomalies (badge)
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';

// ── Query keys ────────────────────────────────────────────────────────────────

export const securityKeys = {
  sessions: (filters) => ['admin', 'security', 'sessions', filters],
  anomalies: (filters) => ['admin', 'security', 'anomalies', filters],
  anomalyCount: () => ['admin', 'security', 'anomalyCount'],
  accessPattern: (userId) => ['admin', 'security', 'accessPattern', userId],
};

// ── useActiveSessions ─────────────────────────────────────────────────────────

/**
 * All active sessions across users, filterable.
 *
 * @param {Object} filters
 * @param {string}  filters.search        — email / name substring
 * @param {string}  filters.platform      — ios | android | web | ''
 * @param {string}  filters.ip_country    — 2-letter country code or ''
 * @param {number}  filters.page
 * @param {number}  filters.limit
 */
export function useActiveSessions(filters = {}) {
  const { authHeaders } = useAuth();
  const {
    search = '',
    platform = '',
    ip_country = '',
    page = 0,
    limit = 20,
  } = filters;

  return useQuery({
    queryKey: securityKeys.sessions({ search, platform, ip_country, page, limit }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (platform) params.set('platform', platform);
      if (ip_country) params.set('ip_country', ip_country);
      params.set('skip', String(page * limit));
      params.set('limit', String(limit));

      const res = await apiClient.get(`/admin/sessions?${params}`, {
        headers: authHeaders(),
      });
      return res.data; // { sessions: [], total: number }
    },
    placeholderData: keepPreviousData,
  });
}

// ── useRevokeUserSessions ─────────────────────────────────────────────────────

/**
 * Revoke all sessions for a specific user.
 */
export function useRevokeUserSessions() {
  const { authHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId) => {
      const res = await apiClient.post(
        `/admin/users/${userId}/revoke-all-sessions`,
        {},
        { headers: authHeaders() }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'security', 'sessions'] });
    },
  });
}

// ── useAnomalies ──────────────────────────────────────────────────────────────

/**
 * Anomaly detection list — filterable by status, severity, rule.
 *
 * @param {Object} filters
 * @param {string}  filters.status    — open | reviewed | dismissed | ''
 * @param {string}  filters.severity  — low | medium | high | ''
 * @param {string}  filters.rule      — high_frequency | multi_ip | impossible_travel | rapid_sequential | off_hours | ''
 * @param {string}  filters.search    — user email / name
 * @param {number}  filters.page
 * @param {number}  filters.limit
 */
export function useAnomalies(filters = {}) {
  const { authHeaders } = useAuth();
  const {
    status = '',
    severity = '',
    rule = '',
    search = '',
    page = 0,
    limit = 20,
  } = filters;

  return useQuery({
    queryKey: securityKeys.anomalies({ status, severity, rule, search, page, limit }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (severity) params.set('severity', severity);
      if (rule) params.set('rule', rule);
      if (search) params.set('search', search);
      params.set('skip', String(page * limit));
      params.set('limit', String(limit));

      const res = await apiClient.get(`/admin/anomalies?${params}`, {
        headers: authHeaders(),
      });
      return res.data; // { anomalies: [], total: number }
    },
    placeholderData: keepPreviousData,
  });
}

// ── useOpenAnomalyCount ───────────────────────────────────────────────────────

/**
 * Returns the count of open anomalies for the sidebar badge.
 * Polling interval: 60 seconds.
 */
export function useOpenAnomalyCount() {
  const { authHeaders } = useAuth();

  return useQuery({
    queryKey: securityKeys.anomalyCount(),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('status', 'open');
      params.set('limit', '1');

      const res = await apiClient.get(`/admin/anomalies?${params}`, {
        headers: authHeaders(),
        _silentForbidden: true,
      });
      return res.data?.total ?? 0;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// ── useReviewAnomaly ──────────────────────────────────────────────────────────

/**
 * Review / action an anomaly.
 *
 * @param {string} id      — anomaly _id
 * @param {string} action  — 'dismiss' | 'freeze_account' | 'revoke_sessions' | 'investigate'
 * @param {string} notes   — optional admin notes
 */
export function useReviewAnomaly() {
  const { authHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, action, notes = '' }) => {
      const res = await apiClient.post(
        `/admin/anomalies/${id}/review`,
        { action, notes },
        { headers: authHeaders() }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'security', 'anomalies'] });
      queryClient.invalidateQueries({ queryKey: securityKeys.anomalyCount() });
    },
  });
}

// ── useUserAccessPattern ──────────────────────────────────────────────────────

/**
 * Hourly access pattern for a specific user.
 * Returns a 24-element array: { hour: 0-23, count: number }[]
 */
export function useUserAccessPattern(userId) {
  const { authHeaders } = useAuth();

  return useQuery({
    queryKey: securityKeys.accessPattern(userId),
    queryFn: async () => {
      const res = await apiClient.get(
        `/admin/users/${userId}/access-pattern`,
        { headers: authHeaders() }
      );
      return res.data; // { pattern: [{hour, count}], anomalies: [] }
    },
    enabled: !!userId,
  });
}
